import {
  clamp,
  dot,
  exp,
  Fn,
  float,
  If,
  instanceIndex,
  Loop,
  length,
  log,
  max,
  min,
  mix,
  normalize,
  pow,
  screenUV,
  select,
  sqrt,
  storageTexture3D,
  texture3D,
  uint,
  uniform,
  uvec3,
  vec2,
  vec3,
  vec4,
} from 'three/tsl';
import * as THREE from 'three/webgpu';
import type { LightRig } from '../lighting/LightRig';
import type { HollowDef } from '../universe/HollowDef';
import type { AtmosphereDef } from './AtmosphereDef';

type N = any;
const SUBSTEPS = 3;
const NEAR = 1;
/** Hollows bigger than this keep their air in a thin shell on the wall (see `thinShell`). */
const THIN_SHELL_RADIUS = 100_000;

const _right = new THREE.Vector3();
const _up = new THREE.Vector3();
const _fwd = new THREE.Vector3();

/**
 * Inverted atmosphere. There is no sky inside a hollow: every view ray ends on
 * terrain, so this is pure aerial perspective. Each frame a compute pass marches
 * one ray per froxel column through the hollow's air (exponential density from
 * the wall inward), lit by the central sun through the light rig's veil and by
 * integrating-sphere bounce light, and stores accumulated in-scatter and
 * transmittance per depth slice. The apply pass then looks up each pixel's depth.
 */
export class Atmosphere {
  readonly scatterTex: THREE.Storage3DTexture;
  readonly transTex: THREE.Storage3DTexture;
  readonly far: number;
  /**
   * Titan-sized hollows: the air is a thin shell on the wall, a scale height of a few km in a
   * hollow hundreds of km across. A ray looking up crosses our air, then near-vacuum, then
   * the far side's air in its last few km, at the end of a depth slice hundreds of km long.
   * Point samples missed that air or smeared it over whole steps: froxel-sized noise, and a
   * dusk band of blocks. So each column's slices end exactly at the wall, and each step
   * integrates the density between its ends (see buildCompute).
   */
  readonly thinShell: boolean;
  private readonly compute: N;
  private compiled: Promise<void> | null = null;
  private readonly camPos = uniform(new THREE.Vector3());
  private readonly camRight = uniform(new THREE.Vector3(1, 0, 0));
  private readonly camUp = uniform(new THREE.Vector3(0, 1, 0));
  private readonly camFwd = uniform(new THREE.Vector3(0, 0, -1));
  private readonly tanHalf = uniform(new THREE.Vector2(1, 1));
  private readonly ambientRadiance = uniform(new THREE.Color(0, 0, 0));
  // The scene camera's clip planes (camera* nodes in a post pass refer to the quad camera).
  private readonly camNear = uniform(0.05);
  private readonly camFar = uniform(1e7);
  /** |camera|² − R², in f64 on the CPU (f32 loses it to cancellation near the wall). */
  private readonly wallC = uniform(0);
  /** Thin shell: the shortest distance a column's slices reach (see update). */
  private readonly minFar = uniform(1e5);
  /** 0 = normal, 1 = show in-scatter ×8, 2 = show transmittance. */
  readonly debugView = uniform(0);
  /** Overall multiplier for the in-scattered light (artistic; starts at the def's). */
  readonly scatterBoost = uniform(1);
  /**
   * How open to the sky the camera is: 1 in the hollow, 0 deep in a cave, where no
   * daylight reaches the air and its haze fades out.
   */
  readonly open = uniform(1);

  constructor(
    private readonly renderer: THREE.WebGPURenderer,
    readonly hollow: HollowDef,
    readonly def: AtmosphereDef,
    readonly rig: LightRig,
    readonly dims: [number, number, number],
  ) {
    this.thinShell = hollow.radius > THIN_SHELL_RADIUS;
    const [X, Y, D] = dims;
    const make = () => {
      const t = new THREE.Storage3DTexture(X, Y, D);
      t.type = THREE.HalfFloatType;
      t.format = THREE.RGBAFormat;
      t.magFilter = THREE.LinearFilter;
      t.minFilter = THREE.LinearFilter;
      t.wrapS = t.wrapT = t.wrapR = THREE.ClampToEdgeWrapping;
      t.generateMipmaps = false;
      return t;
    };
    this.scatterTex = make();
    this.transTex = make();
    this.scatterBoost.value = def.scatterBoost ?? 1;
    this.far = hollow.radius * 2.25;
    this.compute = this.buildCompute().compute(X * Y);
  }

  /** Per frame, before rendering: camera state, then run the froxel pass. */
  /** Compile the froxel compute in the background (once), before this air's first update. */
  precompile(): Promise<void> {
    this.compiled ??= this.renderer.compileComputeAsync(this.compute).then(() => {});
    return this.compiled;
  }

  /** Give the froxel volumes' GPU memory back while this hollow is unloaded (restore() undoes it). */
  release(): void {
    this.scatterTex.setSize(1, 1, 1);
    this.transTex.setSize(1, 1, 1);
  }

  restore(): void {
    const [x, y, d] = this.dims;
    this.scatterTex.setSize(x, y, d);
    this.transTex.setSize(x, y, d);
  }

  update(camera: THREE.PerspectiveCamera, cameraUniverse: THREE.Vector3): void {
    this.camPos.value.subVectors(cameraUniverse, this.hollow.center);
    _right.set(1, 0, 0).applyQuaternion(camera.quaternion);
    _up.set(0, 1, 0).applyQuaternion(camera.quaternion);
    _fwd.set(0, 0, -1).applyQuaternion(camera.quaternion);
    this.camRight.value.copy(_right);
    this.camUp.value.copy(_up);
    this.camFwd.value.copy(_fwd);
    const ty = Math.tan(THREE.MathUtils.degToRad(camera.fov) / 2);
    this.tanHalf.value.set(ty * camera.aspect, ty);
    this.camNear.value = camera.near;
    this.camFar.value = camera.far;
    const amb = this.rig.ambientIrradiance(new THREE.Color());
    this.ambientRadiance.value
      .setRGB(amb.r / Math.PI, amb.g / Math.PI, amb.b / Math.PI)
      .multiplyScalar(this.def.ambientScatter);
    if (this.thinShell) {
      const R = this.hollow.radius;
      const H = this.def.rayleighHeight;
      const r = this.camPos.value.length();
      this.wallC.value = (r - R) * (r + R);
      // From the ground, rays that meet the wall within √(8RH) (a chord whose middle rises
      // one scale height) stay in the dense air all the way, and near the horizon that length
      // changes several-fold from one froxel row to the next: those columns keep a common
      // slicing, so neighbours blend at equal distances. From high above the air, every ray
      // ends in a thin layer, and the common slicing gives way.
      this.minFar.value = Math.max(Math.sqrt(8 * R * H) * Math.exp(-Math.max(R - r, 0) / H), 100);
    }
    this.renderer.compute(this.compute);
  }

  private buildCompute() {
    const [X, Y, D] = this.dims;
    const R = this.hollow.radius;
    const a = this.def;
    const rig = this.rig;
    const scatterTex = this.scatterTex;
    const transTex = this.transTex;
    const ratio = (this.far / NEAR) ** (1 / (D - 1));
    const betaR = vec3(...a.rayleigh);
    const betaM = float(a.mie);
    const betaMext = float(a.mie * (1 + a.mieAbsorption));
    const g = a.mieG;
    const sunI = rig.sunIrradiance * R * R; // point-source intensity
    const camPos = this.camPos;
    const camRight = this.camRight;
    const camUp = this.camUp;
    const camFwd = this.camFwd;
    const tanHalf = this.tanHalf;
    const ambient = this.ambientRadiance;
    const boost = this.scatterBoost;

    return Fn(() => {
      const idx = instanceIndex;
      const x = idx.mod(uint(X));
      const y = idx.div(uint(X));
      If(y.lessThan(uint(Y)), () => {
        const ndc = vec2(
          float(x).add(0.5).div(X).mul(2).sub(1),
          float(1).sub(float(y).add(0.5).div(Y).mul(2)),
        );
        const dir = normalize(
          camRight
            .mul(ndc.x.mul(tanHalf.x))
            .add(camUp.mul(ndc.y.mul(tanHalf.y)))
            .add(camFwd),
        ).toVar();
        const T = vec3(1).toVar();
        const S = vec3(0).toVar();
        const tPrev = float(0).toVar();

        /** Light scattered toward the camera per metre at p, where the densities are (dR, dM). */
        const inscatterAt = (p: N, r: N, dR: N, dM: N): N => {
          const sunDir = p.div(r).negate();
          const mu = dot(dir, sunDir);
          const phaseR = mu
            .mul(mu)
            .add(1)
            .mul(3 / (16 * Math.PI));
          const gg = g * g;
          const phaseM = float((1 - gg) / (4 * Math.PI)).div(pow(float(1 + gg).sub(mu.mul(2 * g)), 1.5));
          const scatR = betaR.mul(dR);
          const scatM = betaM.mul(dM);
          const vis = rig.veilAt(p.div(r));
          let inscatter: N;
          if (sunI > 0) {
            const light = rig.lightColorAt(p).mul(float(sunI).div(max(r.mul(r), 2500)));
            // Bounce light in the air: use its brightness only, so the air stays sky-blue
            // (Rayleigh) instead of taking on the green of the world's average albedo.
            const ambientLum = dot(ambient as N, vec3(0.2126, 0.7152, 0.0722));
            // Dusk band: air under the veil's rim glows warm, drawing a sunset line
            // across the sky wherever the terminator is.
            const rimGlow = float(1).sub(vis.mul(2).sub(1).abs());
            const dusk = vec3(rig.duskColor as N).mul(
              pow(rimGlow, 1.3).mul(
                float(sunI)
                  .div(max(r.mul(r), 2500))
                  .mul(1.1),
              ),
            );
            inscatter = scatR
              .mul(phaseR)
              .add(scatM.mul(phaseM))
              .mul(light)
              .add(scatR.add(scatM).mul(ambientLum).mul(vis.mul(0.75).add(0.25)))
              .add(scatR.mul(0.45).add(scatM).mul(dusk));
          } else {
            // No sun: the air only carries the walls' own light, in its colour.
            inscatter = scatR.add(scatM).mul(ambient).mul(vis.mul(0.75).add(0.25));
          }
          if (a.airGlow) {
            inscatter = inscatter.add(
              vec3(...a.airGlow)
                .mul(dM)
                .mul(rig.glowAt(p.div(r))),
            );
          }
          return inscatter.mul(boost);
        };

        /** March `len` metres of air with densities (dR, dM), lit as at p. */
        const addStep = (p: N, r: N, dR: N, dM: N, len: N) => {
          const sigmaT = betaR.mul(dR).add(betaMext.mul(dM));
          const inscatter = inscatterAt(p, r, dR, dM);
          const trans = exp(sigmaT.mul(len).negate());
          // Energy-conserving integration over the step (Hillaire 2020).
          S.addAssign(T.mul(inscatter.sub(inscatter.mul(trans)).div(max(sigmaT, vec3(1e-12)))));
          T.mulAssign(trans);
        };

        const store = (i: N) => {
          storageTexture3D(scatterTex, uvec3(x, y, uint(i)) as N, vec4(S, 1)).toStack();
          storageTexture3D(transTex, uvec3(x, y, uint(i)) as N, vec4(T, 1)).toStack();
        };

        if (!this.thinShell) {
          // Slices from NEAR to `far`, log-spaced; density sampled mid-step.
          Loop(D, ({ i }: { i: N }) => {
            const tNext = float(NEAR).mul(pow(float(ratio), float(i)));
            const ds = tNext.sub(tPrev).div(SUBSTEPS);
            for (let k = 0; k < SUBSTEPS; k++) {
              const t = tPrev.add(ds.mul(k + 0.5));
              const p = camPos.add(dir.mul(t));
              const r = max(length(p), 1.0);
              const alt = max(float(R).sub(r), 0);
              addStep(p, r, exp(alt.div(-a.rayleighHeight)), exp(alt.div(-a.mieHeight)), ds);
            }
            tPrev.assign(tNext);
            store(i);
          });
          return;
        }

        // Thin shell. The slices run from NEAR to where this column's ray meets the wall
        // (no further than `far`, no nearer than minFar), log-spaced. Steps stop at the wall:
        // beyond it is rock, which a clamped altitude would count as the densest air.
        const tWall = this.wallDistance(dir).toVar();
        const logEnd = log(clamp(tWall, this.minFar, this.far).div(NEAR)).toVar();
        const altAt = (t: N) => max(float(R).sub(length(camPos.add(dir.mul(t)))), 0);
        const altPrev = altAt(float(0)).toVar();
        Loop(D, ({ i }: { i: N }) => {
          const tNext = float(NEAR).mul(exp(logEnd.mul(float(i).div(D - 1))));
          const ds = tNext.sub(tPrev).div(SUBSTEPS);
          for (let k = 0; k < SUBSTEPS; k++) {
            const ta = min(tPrev.add(ds.mul(k)), tWall);
            const len = min(tPrev.add(ds.mul(k + 1)), tWall)
              .sub(ta)
              .toVar();
            const altA = altPrev.toVar();
            const altB = altAt(ta.add(len)).toVar();
            // A step can be hundreds of km long with all its air in the last few. Density is
            // exp(−alt/H) and the altitude changes about linearly along a step, so take the
            // exact mean between its ends; and light the step at its density-weighted middle,
            // which near the far wall is close to the wall (the dusk band's colour is there).
            const meanDensity = (H: number) => {
              const x = altA.sub(altB).div(H);
              const dA = exp(altA.div(-H));
              const dB = exp(altB.div(-H));
              const steep = x.abs().greaterThan(1e-3);
              return select(steep, dB.sub(dA).div(select(steep, x, 1)), dA.add(dB).mul(0.5));
            };
            const dR = meanDensity(a.rayleighHeight).toVar();
            const dM = meanDensity(a.mieHeight).toVar();
            const xc = clamp(altA.sub(altB).div(a.rayleighHeight), -60, 60);
            const steep = xc.abs().greaterThan(1e-3);
            const xs = select(steep, xc, 1);
            const centre = select(
              steep,
              float(1)
                .div(float(1).sub(exp(xs.negate())))
                .sub(float(1).div(xs)),
              0.5,
            );
            const p = camPos.add(dir.mul(ta.add(len.mul(centre))));
            addStep(p, max(length(p), 1.0), dR, dM, len);
            altPrev.assign(altB);
          }
          tPrev.assign(tNext);
          store(i);
        });
      });
    })();
  }

  /** Distance from the camera to the wall (sea level) along a hollow-frame unit direction. */
  private wallDistance(dir: N): N {
    const b = dot(this.camPos, dir);
    return b.negate().add(sqrt(max(b.mul(b).sub(this.wallC), 0)));
  }

  /** World-space (hollow frame) view ray for a screen UV. */
  viewRay(uv: N): N {
    const ndc = vec2(uv.x.mul(2).sub(1), float(1).sub(uv.y.mul(2)));
    return normalize(
      this.camRight
        .mul(ndc.x.mul(this.tanHalf.x))
        .add(this.camUp.mul(ndc.y.mul(this.tanHalf.y)))
        .add(this.camFwd),
    );
  }

  /** Distance along the view ray from a reversed-Z depth sample. */
  distanceFromDepth(depth: N, uv: N): N {
    // Reversed-Z: d = n(f/viewDepth − 1)/(f − n)  ⇒  viewDepth = f·n / (n + d(f − n)).
    const n = this.camNear;
    const fa = this.camFar;
    const viewDepth = fa.mul(n).div(n.add(depth.mul(fa.sub(n))));
    const ndc = vec2(uv.x.mul(2).sub(1), float(1).sub(uv.y.mul(2)));
    const ray = vec3(ndc.x.mul(this.tanHalf.x), ndc.y.mul(this.tanHalf.y), 1);
    return viewDepth.mul(length(ray));
  }

  /**
   * Aerial perspective between the camera and a distance along the pixel ray (`ray`, the
   * pixel's viewRay, if the caller already has it).
   */
  lookup(uv: N, dist: N, ray: N = null): { S: N; T: N } {
    const D = this.dims[2];
    // The slices' log-distance range: to `far`, or in a thin shell to this ray's own wall
    // (as the compute pass placed them for the columns around it).
    const logEnd = this.thinShell
      ? log(clamp(this.wallDistance(ray ?? this.viewRay(uv)), this.minFar, this.far).div(NEAR))
      : Math.log(this.far / NEAR);
    const w = clamp(log(max(dist, NEAR).div(NEAR)).div(logEnd), 0, 1);
    const coord = vec3(
      uv.x,
      uv.y,
      w
        .mul(D - 1)
        .add(0.5)
        .div(D),
    );
    // Explicit level: no mipmaps anyway, and it may be called inside branches.
    return {
      S: texture3D(this.scatterTex, coord).level(float(0)).rgb,
      T: texture3D(this.transTex, coord).level(float(0)).rgb,
    };
  }

  /** Camera position relative to the hollow centre (uniform). */
  get cameraLocal(): N {
    return this.camPos;
  }

  /** Post-processing node: scene colour × transmittance + in-scatter, by pixel depth. */
  apply(colorNode: N, depthNode: N): N {
    return Fn(() => {
      const uv = screenUV;
      const depth = depthNode.sample(uv).r;
      const dist = this.distanceFromDepth(depth, uv);
      const { S, T } = this.lookup(uv, dist);
      const c = colorNode.isTextureNode ? colorNode.sample(uv) : colorNode;
      const open = this.open;
      const final = c.rgb.mul(mix(vec3(1), T, open)).add(S.mul(open));
      const dbg = this.debugView;
      return vec4(dbg.equal(1).select(S.mul(8), dbg.equal(2).select(T, final)) as N, 1);
    })();
  }
}
